<template>
  <ElFormItem
    ref="elFormItem"
    :prop="name"
    :label="label"
    :double="doubleWidth"
    class="fi-select"
  >
    <ElSelect
      ref="selectRef"
      v-model="bindValue"
      v-bind="$attrs"
      :filterable="hasData"
      :filter-method="filter"
      :clearable="clearable"
      :validate-event="false"
      @visible-change="clearFilter"
      @change="valueChange"
    >
      <EditableOptions
        :options-data="optionsData || []"
        :editor="editor"
        :reload="reload"
      >
        <slot />
      </EditableOptions>
    </ElSelect>

    <HelpIcon :help="help" />
  </ElFormItem>
</template>

<script lang="ts" setup>
/**
 * ### 下拉组件
 * <warning> 获取下拉数据可从url/transform/callback三种方式中三选一
 * <warning> 可通过默认插槽渲染下拉项
 * 传入未指定属性会透传到 ElSelect 组件中
 */
import { ref, watch } from 'vue';
import { ElFormItem, ElSelect } from 'element-plus';
import { ListId } from '../../../core/types';
import {
  pinyinFilterData,
  PinyinFilterCallback
} from '../../../core/CommonInternal';
import { useShareData } from '../../../core/SharedData';
import { isTransformRawId } from '../../../core/Transform';
import { getLayout, saveLayout } from '../../../core/LayoutPersistent';
import { DropdownEditor } from '../../../common/DropdownEditor';
import EditableOptions from '../../../common/EditableOptions.vue';
import HelpIcon from '../../../common/HelpIcon.vue';
import { toInteger, isString, isNumber, isEqual } from 'lodash-es';

const props = withDefaults(
  defineProps<{
    /**
     * ### post参数名
     */
    name: string;
    /**
     * ### 表单项名
     */
    label: string;
    /**
     * ### 默认值
     */
    defaultValue?: number;
    /**
     * ### 获取下拉数据的url
     * <warning> 最低优先级
     */
    url?: string;
    /**
     * ### 获取下拉数据的数据源（变换）
     * <warning> 优先级最高
     */
    transform?: string;
    /**
     * ### 获取下拉数据的函数
     * <warning> 优先级高于url低于transform
     */
    callback?: PinyinFilterCallback;
    /**
     * ### 如若使用callback方式获取下拉数据 该选项会从form model中获取同名key作为callback参数
     */
    callbackParams?: string[];
    /**
     * ### 排除下拉数据中的特定ID
     */
    excludes?: number[];
    /**
     * ### 保持id类型不变, 默认会转成number
     */
    rawId?: boolean;
    /**
     * ### 选中值为0时 是否不向后端发送改字段值
     */
    zeroToUndefined?: boolean;
    /**
     * ### 下拉列表编辑器
     */
    editor?: DropdownEditor;
    /**
     * ### 值发生变化前的校验
     * 抛出错误则阻止值变更
     */
    changeCheck?: (newVal: ListId, oldVal: ListId) => Promise<void>;
    /**
     * ### 是否保存上一次的记录
     */
    persist?: boolean;
    /**
     * ### 双倍宽度
     */
    double?: boolean;
    /**
     * ### 帮助说明
     */
    help?: string;
    /**
     * ### 是否可清除
     */
    clearable?: boolean;
  }>(),
  {
    clearable: true
  }
);

// 如果使用transform, 从transform提取rawId
const rawId = props.transform ? isTransformRawId(props.transform) : props.rawId;

const { optionsData, filter, clearFilter, reload, hasData } = pinyinFilterData(
  props.url,
  props.transform,
  props.callback,
  props.callbackParams,
  true,
  props.excludes,
  props.rawId
);

const formName = useShareData('form', 'name');
const model = useShareData('form', 'model');
// eslint-disable-next-line vue/no-setup-props-destructure
const name = props.name;

// 设置默认值
if (model[name] === undefined) {
  const persistValue = props.persist
    ? getLayout(
        'form',
        ['fi', formName, name],
        undefined,
        rawId ? isString : isNumber
      )
    : undefined;

  if (persistValue !== undefined) {
    model[name] = persistValue;
  } else if (props.defaultValue !== undefined) {
    // eslint-disable-next-line vue/no-setup-props-destructure
    model[name] = props.defaultValue;
  }
}

// 在form中注册
useShareData('form', 'items').$(name)({
  persist: () => {
    if (props.persist) {
      saveLayout('form', ['fi', formName, name], model[name]);
    }
  }
});

// 从model中取值
function modelValue(v: any) {
  if (v === undefined) return v;

  if (rawId) {
    if (props.zeroToUndefined && v == '0') {
      return undefined;
    }

    return v;
  }

  if (v === '') {
    return undefined;
  }

  const num = toInteger(v);
  if (props.zeroToUndefined && num == 0) {
    return undefined;
  }

  return num;
}

// 绑定值
const bindValue = ref();

// 变更前值
let previousValue: any;

// model变更，更新bindValue
watch(
  () => model[name],
  (newValue) => {
    previousValue = modelValue(newValue);
    bindValue.value = previousValue;
  },
  { immediate: true }
);

const elFormItem = ref();
// 选项值变化
async function valueChange(v: any) {
  if (isEqual(v, previousValue)) {
    return;
  }

  if (props.changeCheck) {
    // 执行检查
    try {
      await props.changeCheck(v, previousValue);
      // 更新model
      model[name] = v;
    } catch (e: any) {
      // 异常执行还原
      bindValue.value = previousValue;
      throw e;
    }
  } else {
    model[name] = v;
  }

  // 执行验证
  elFormItem.value?.validate?.('change');
}

// 双倍宽度
const doubleWidth = props.double ? '' : undefined;
</script>

<style lang="scss" scoped>
.fi-select {
  height: var(--fi-line-height);
  font-size: var(--fi-font-size);

  :deep(.el-input) {
    height: var(--fi-line-height);

    --el-input-height: calc(var(--fi-line-height) - 2px);
    --el-font-size-base: var(--fi-font-size);
    --el-input-icon-color: #8f959e;
  }

  :deep(.el-input__wrapper) {
    box-shadow: none;
    border-radius: var(--fi-border-radius);
    border: 1px solid var(--fi-border-color);
    padding-top: 0;
    padding-bottom: 0;
  }

  :deep(.el-input__wrapper:hover) {
    box-shadow: none !important;
    border-color: var(--fi-border-hover-color);
  }

  :deep(.el-input.is-focus) {
    box-shadow: none;
  }

  :deep(.el-input.is-focus .el-input__wrapper) {
    box-shadow: none !important;
    border-color: var(--fi-border-hover-color);
  }

  :deep(.el-input__prefix) {
    max-width: var(--fi-prefix-width);
    margin-right: var(--fi-prefix-right-space);
    padding-left: 12px;
  }

  :deep(.el-input__prefix-inner) {
    display: block;
    max-width: var(--fi-prefix-width);
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  :deep(.el-input__prefix-inner:hover) {
    overflow: visible;
  }

  .el-select + .el-icon {
    margin-left: 8px;
  }
}

.fi-select[double] {
  grid-column-start: span 2;
}
</style>
